/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// cihan
import { alpha, Chip, FormControl, SxProps, Theme, useTheme } from "@mui/material";
import React, { CSSProperties, useState } from "react";
import Columns from "../../layout/Columns";
import Cell from "../../layout/Cell";
import Rows from "../../layout/Rows";
import { CsmText } from "../CsmText";
import "drag-drop-touch";
import { StringOrNumber } from "../../../helpers/StringOrNumber";

export interface ICsmGenericTwoBoxListMultiSelectFieldProps<T, U extends StringOrNumber> {
    id?: string,
    value: U[] | null,
    label: string,
    itemLabel: string,
    availableList: T[],
    wholeList?: T[],
    labelGetter: (e: T) => string | React.ReactNode,
    valueGetter: (e: T) => U,
    onChange: (e: U[]) => void,
    defaultOrderFunc?: (e: U[]) => U[],
    sortable?: boolean,
    error?: boolean,
    fontSize?: number,
    sx?: SxProps<Theme>,
    style?: CSSProperties,
    disabled?: boolean,
    isItemInactive?: (e: T) => boolean,
    isItemDisabled?: (e: U) => boolean,
    labels: {
        secilmeyenler?: string,
        secilenler?: string,
        listeyeEklemekIcinTıklayabilirsiniz?: string,
        siralarınıDegistirmekIcinTasiyabilirsinizListedenCikartmakIcinTiklayabilirsiniz?: string
    }
}

export default function CsmGenericTwoBoxListMultiSelectField<T, U extends StringOrNumber>(props: ICsmGenericTwoBoxListMultiSelectFieldProps<T, U>) {
    if (props.sortable)
        return Sortable_GenericTwoBoxListMultiSelectField(props);
    else
        return Unsortable_GenericTwoBoxListMultiSelectField(props);
}

function Sortable_GenericTwoBoxListMultiSelectField<T, U extends StringOrNumber>(props: ICsmGenericTwoBoxListMultiSelectFieldProps<T, U>) {
    const { labels, defaultOrderFunc, sortable, isItemDisabled, isItemInactive, id, value, label, itemLabel, availableList, wholeList, labelGetter, valueGetter, onChange, error, fontSize, sx, disabled, style } = props;

    const wholeListToUse = wholeList ?? availableList;

    const [movingValue, setMovingValue] = useState<U | null>(null);
    const [movingOrderedList, setMovingOrderedList] = useState<U[]>(value?.slice() ?? []);

    const theme = useTheme();

    const color_Black023 = alpha(theme.palette.common.black, 0.23)
    const color_Black060 = alpha(theme.palette.common.black, 0.60)
    const color_GrayLight2 = theme.palette.grey.A400
    const color_Red = theme.palette.error.main;
    const color_PrimaryLight = theme.palette.primary.light;

    function calculateNewOrderedList2(list: U[], source: U, dest: U) {
        const newList = list.slice();
        const sourceIndex = list.indexOf(source);
        const destIndex = list.indexOf(dest);

        if (newList.length === 0)
            return [];

        const d = newList[sourceIndex];
        newList.splice(sourceIndex, 1);
        newList.splice(destIndex, 0, d);
        return newList;
    }

    const chipClicked = (newValue: U) => {
        const newIdList = value?.slice() ?? [];
        if (isSelected(newValue))
            newIdList.remove(newValue);
        else
            newIdList.push(newValue);

        onChange(newIdList);
        setMovingOrderedList(newIdList);
    }

    const onChipDrop = (value: U, event: any) => {
        // taşıma işlemi, bu kontrolün üzerinde tamamlanınca tetiklenir
        event.preventDefault();
        setMovingValue(null);
        onChange(movingOrderedList);
    }

    const onDragEnter = (value: U, event: any) => {
        // taşıma işlemi, bu controlün üzerinden geçmeye başladığında tetiklenir
        if (value !== movingValue && !!movingValue) {
            setMovingOrderedList(calculateNewOrderedList2(movingOrderedList, movingValue!, value));
        }
        event.preventDefault();
    }

    const onDragOver = (value: U, event: any) => {
        // taşıma işlemi, bu controlün üzerinden geçerken (pek çok kere) tetiklenir
        event.preventDefault();
    }

    const onDragStart = (value: U, event: any) => {
        // taşıma işlemi bu control üzerinden tutularak başlayınca tetiklenir
        setMovingValue(value);
    }

    const onDragEnd = (value: U, event: any) => {
        // taşıma işleminden vazgeçilince (veya tamamlanınca) tetiklenir
        setMovingValue(null);
    }

    const isSelected = (e: U) => (value?.contains(e) ?? false);
    const isDisabled = (e: U) => (isItemDisabled ? isItemDisabled(e) : false);
    const isLight = (e: T) => isItemInactive ? isItemInactive(e) : false;

    function act(arg0: string, arg1: () => void): void {
        arg1();
    }

    const getSelectedItemLabel = (index: number, itemValue: StringOrNumber) => {
        return sortable ?
            (index + 1).toFixed(0) + ". " + labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim()
            :
            labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim();
    }

    function orderIfNecessary(items: U[]): U[] {
        return sortable ? items : !defaultOrderFunc ? items : defaultOrderFunc(items);
    }

    return (
        <FormControl disabled={disabled} fullWidth style={{ marginTop: 0, ...style }} >
            <fieldset style={{ borderRadius: "4px", borderColor: error ? color_Red : color_Black023, borderWidth: "1px" }}>
                <legend style={{ fontSize: "12px", fontWeight: 400, color: error ? color_Red : color_Black060, }}>{label}</legend>
                <Columns>
                    <Rows flex mt={-13} mb={-11} pt={13} pb={11} hasBlack23RightBorder style={{ minWidth: "0px" }}>
                        <Cell mt={-10} ml={-13} hasBlack23BottomBorder>
                            <Columns style={{ alignContent: "center", justifyContent: "center", alignItems: "center" }}>
                                <CsmText style={{ textAlign: "center" }}>{labels?.secilmeyenler ?? "Seçilmeyenler"}</CsmText>
                                <CsmText smallNote style={{ marginLeft: "5px" }}>{`${availableList.length - movingOrderedList.length} ${itemLabel}`}</CsmText>
                            </Columns>
                        </Cell>
                        <Cell style={{ padding: "10px" }}>
                            <Columns wrap>
                                {availableList.where(e => !isSelected(valueGetter(e))).select(e => valueGetter(e)).map(itemValue =>
                                    <Chip disabled={isDisabled(itemValue)} onClick={_ => chipClicked(itemValue)} key={itemValue} style={{ margin: "5px" }} label={labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim()} />
                                )}
                            </Columns>
                        </Cell>
                        <Cell flex></Cell>
                        <Columns mb={-12} ml={-12}>
                            <CsmText smallNote italic style={{ marginLeft: "5px", color: color_GrayLight2 }}>{labels?.listeyeEklemekIcinTıklayabilirsiniz ?? "Listeye eklemek için tıklayabilirsiniz"}</CsmText>
                        </Columns>
                    </Rows>
                    <Rows flex style={{ minWidth: "0px" }}>
                        <Cell mt={-10} mr={-13} hasBlack23BottomBorder>
                            <Columns style={{ alignContent: "center", justifyContent: "center", alignItems: "center" }}>
                                <CsmText style={{ textAlign: "center" }}>{labels?.secilenler ?? "Seçilenler"}</CsmText>
                                <CsmText smallNote style={{ marginLeft: "5px" }}>{`${movingOrderedList.length} ${itemLabel}`}</CsmText>
                            </Columns>
                        </Cell>
                        <Cell style={{ padding: "10px" }}>
                            <Columns wrap>
                                {orderIfNecessary(movingOrderedList).map((itemValue, index) =>
                                    <Chip draggable={!!sortable} id={itemValue.toString()}
                                        onDragStart={!sortable ? undefined : e => act("onDragStart", () => onDragStart(itemValue, e))}
                                        onDragOver={!sortable ? undefined : e => act("onDragOver", () => onDragOver(itemValue, e))}
                                        onDragEnter={!sortable ? undefined : e => act("onDragEnter", () => onDragEnter(itemValue, e))}
                                        onDragEnd={!sortable ? undefined : e => act("onDragEnd", () => onDragEnd(itemValue, e))}
                                        onDrop={!sortable ? undefined : e => act("onDrop", () => onChipDrop(itemValue, e))}
                                        onClick={_ => chipClicked(itemValue)}
                                        key={itemValue}
                                        style={{ margin: "5px", backgroundColor: movingValue === itemValue ? color_PrimaryLight : undefined }}
                                        label={getSelectedItemLabel(index, itemValue)} />
                                )}
                            </Columns>
                        </Cell>
                        <Cell flex></Cell>
                        <Columns mb={-12}>
                            <CsmText smallNote italic style={{ marginLeft: "5px", color: color_GrayLight2 }}>{labels?.siralarınıDegistirmekIcinTasiyabilirsinizListedenCikartmakIcinTiklayabilirsiniz ?? "Sıralarını değiştirmek için taşıyabilirsiniz. Listeden çıkartmak için tıklayabilirsiniz"}</CsmText>
                        </Columns>
                    </Rows>
                </Columns>
            </fieldset>
        </FormControl>
    );
}

function Unsortable_GenericTwoBoxListMultiSelectField<T, U extends StringOrNumber>(props: ICsmGenericTwoBoxListMultiSelectFieldProps<T, U>) {
    const { labels, defaultOrderFunc, sortable, isItemDisabled, isItemInactive, id, value, label, itemLabel, wholeList, availableList, labelGetter, valueGetter, onChange, error, fontSize, sx, disabled, style } = props;

    const wholeListToUse = wholeList ?? availableList;

    const theme = useTheme();

    const color_Black023 = alpha(theme.palette.common.black, 0.23)
    const color_Black060 = alpha(theme.palette.common.black, 0.60)
    const color_GrayLight2 = theme.palette.grey.A400
    const color_Red = theme.palette.error.main;

    const movingOrderedList = value?.slice() ?? [];

    const chipClicked = (newValue: U) => {
        const newIdList = value?.slice() ?? [];
        if (isSelected(newValue))
            newIdList.remove(newValue);
        else
            newIdList.push(newValue);

        onChange(newIdList);
    }

    const isSelected = (e: U) => (value?.contains(e) ?? false);
    const isDisabled = (e: U) => (isItemDisabled ? isItemDisabled(e) : false);
    const isLight = (e: T) => isItemInactive ? isItemInactive(e) : false;

    const getSelectedItemLabel = (index: number, itemValue: StringOrNumber) => {
        return sortable ?
            (index + 1).toFixed(0) + ". " + labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim()
            :
            labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim();
    }

    function orderIfNecessary(items: U[]): U[] {
        return sortable ? items : !defaultOrderFunc ? items : defaultOrderFunc(items);
    }

    return (
        <FormControl disabled={disabled} fullWidth style={{ marginTop: 0, ...style }} >
            <fieldset style={{ borderRadius: "4px", borderColor: error ? color_Red : color_Black023, borderWidth: "1px" }}>
                <legend style={{ fontSize: "12px", fontWeight: 400, color: error ? color_Red : color_Black060, }}>{label}</legend>
                <Columns>
                    <Rows flex mt={-13} mb={-11} pt={13} pb={11} hasBlack23RightBorder style={{ minWidth: "0px" }}>
                        <Cell mt={-10} ml={-13} hasBlack23BottomBorder>
                            <Columns style={{ alignContent: "center", justifyContent: "center", alignItems: "center" }}>
                                <CsmText style={{ textAlign: "center" }}>{labels?.secilmeyenler ?? "Seçilmeyenler"}</CsmText>
                                <CsmText smallNote style={{ marginLeft: "5px" }}>{`${availableList.length - movingOrderedList.length} ${itemLabel}`}</CsmText>
                            </Columns>
                        </Cell>
                        <Cell style={{ padding: "10px" }}>
                            <Columns wrap>
                                {availableList.where(e => !isSelected(valueGetter(e))).select(e => valueGetter(e)).map(itemValue =>
                                    <Chip disabled={isDisabled(itemValue)} onClick={_ => chipClicked(itemValue)} key={itemValue} style={{ margin: "5px" }} label={labelGetter(wholeListToUse.single(e => valueGetter(e) === itemValue))?.toString().trim()} />
                                )}
                            </Columns>
                        </Cell>
                        <Cell flex></Cell>
                        <Columns mb={-12} ml={-12}>
                            <CsmText smallNote italic style={{ marginLeft: "5px", color: color_GrayLight2 }}>{labels?.listeyeEklemekIcinTıklayabilirsiniz ?? "Listeye eklemek için tıklayabilirsiniz"}</CsmText>
                        </Columns>
                    </Rows>
                    <Rows flex style={{ minWidth: "0px" }}>
                        <Cell mt={-10} mr={-13} hasBlack23BottomBorder>
                            <Columns style={{ alignContent: "center", justifyContent: "center", alignItems: "center" }}>
                                <CsmText style={{ textAlign: "center" }}>{labels?.secilenler ?? "Seçilenler"}</CsmText>
                                <CsmText smallNote style={{ marginLeft: "5px" }}>{`${movingOrderedList.length} ${itemLabel}`}</CsmText>
                            </Columns>
                        </Cell>
                        <Cell style={{ padding: "10px" }}>
                            <Columns wrap>
                                {orderIfNecessary(movingOrderedList).map((itemValue, index) =>
                                    <Chip id={itemValue.toString()}
                                        onClick={_ => chipClicked(itemValue)}
                                        key={itemValue}
                                        style={{ margin: "5px" }}
                                        label={getSelectedItemLabel(index, itemValue)} />
                                )}
                            </Columns>
                        </Cell>
                        <Cell flex></Cell>
                        <Columns mb={-12}>
                            <CsmText smallNote italic style={{ marginLeft: "5px", color: color_GrayLight2 }}>{labels?.siralarınıDegistirmekIcinTasiyabilirsinizListedenCikartmakIcinTiklayabilirsiniz ?? "Sıralarını değiştirmek için taşıyabilirsiniz. Listeden çıkartmak için tıklayabilirsiniz"}</CsmText>
                        </Columns>
                    </Rows>
                </Columns>
            </fieldset>
        </FormControl>
    );
}